import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';

export type LegalEntityCatalogSliceState = {
  countries: GeneralCatalogItem[];
  occupations: GeneralCatalogItem[];
  countryStates: GeneralCatalogItem[];
  cities: GeneralCatalogItem[];
  townships: GeneralCatalogItem[];
  civilStatus: GeneralCatalogItem[];
  birthPlaces: GeneralCatalogItem[];
  restrictedBirthPlaces: GeneralCatalogItem[];
  beneficiaryNationality: GeneralCatalogItem[];
  beneficiaryBirthPlace: GeneralCatalogItem[];
  beneficiaryOccupations: GeneralCatalogItem[];
  economicActivities: GeneralCatalogItem[];
  beneficiaryCountries: GeneralCatalogItem[];
  beneficiaryCountryStates: GeneralCatalogItem[];
  beneficiaryCities: GeneralCatalogItem[];
  beneficiaryTownships: GeneralCatalogItem[];
  countriesMap: { [key: string]: string };
  beneficiaryCountriesMap: { [key: string]: string };
  occupationsMap: { [key: string]: string };
  beneficiaryOccupationsMap: { [key: string]: string };
  economicActivitiesMap: { [key: string]: string };
  beneficiaryNationalityMap: { [key: string]: string };
  beneficiaryBirthPlaceMap: { [key: string]: string };
};

export const initialState: LegalEntityCatalogSliceState = {
  countries: [],
  occupations: [],
  cities: [],
  countryStates: [],
  townships: [],
  beneficiaryCountries: [],
  beneficiaryCities: [],
  beneficiaryCountryStates: [],
  beneficiaryTownships: [],
  countriesMap: {},
  beneficiaryCountriesMap: {},
  occupationsMap: {},
  civilStatus: [],
  birthPlaces: [],
  restrictedBirthPlaces: [],
  beneficiaryNationality: [],
  beneficiaryBirthPlace: [],
  beneficiaryOccupations: [],
  economicActivities: [],
  beneficiaryNationalityMap: {},
  beneficiaryBirthPlaceMap: {},
  beneficiaryOccupationsMap: {},
  economicActivitiesMap: {},
};

const legalEntityCatalogSlice = createSlice({
  name: 'legalEntityCatalog',
  initialState,
  reducers: {
    updateCountries: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.countries = action.payload;
    },
    updateOccupations: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.occupations = action.payload;
    },
    updateBeneficiaryOccupations: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryOccupations = action.payload;
    },
    updateEconomicActivities: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.economicActivities = action.payload;
    },
    updateCountryStates: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.countryStates = action.payload;
    },
    updateCities: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.cities = action.payload;
    },
    updateTownships: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.townships = action.payload;
    },
    updateBeneficiaryCountries: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryCountries = action.payload;
    },
    updateBeneficiaryCountryStates: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryCountryStates = action.payload;
    },
    updateBeneficiaryCities: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryCities = action.payload;
    },
    updateBeneficiaryTownships: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryTownships = action.payload;
    },
    updateBeneficiaryNationality: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryNationality = action.payload;
    },
    updateBeneficiaryBirthPlace: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.beneficiaryBirthPlace = action.payload;
    },
    updateCivilStatus: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.civilStatus = action.payload;
    },
    updateBirthPlaces: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.birthPlaces = action.payload;
    },
    updateRestrictedBirthPlaces: (
      state,
      action: PayloadAction<GeneralCatalogItem[]>,
    ): void => {
      state.restrictedBirthPlaces = action.payload;
    },
    updateCountriesMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.countriesMap = action.payload;
    },
    updateBeneficiaryCountriesMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.beneficiaryCountriesMap = action.payload;
    },
    updateBeneficiaryNationalityMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.beneficiaryNationalityMap = action.payload;
    },
    updateBeneficiaryBirthPlaceMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.beneficiaryBirthPlaceMap = action.payload;
    },
    updateOccupationsMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.occupationsMap = action.payload;
    },
    updateBeneficiaryOccupationsMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.beneficiaryOccupationsMap = action.payload;
    },
    updateEconomicActivitiesMap: (
      state,
      action: PayloadAction<{ [key: string]: string }>,
    ): void => {
      state.economicActivitiesMap = action.payload;
    },
    overwriteLegalEntityCatalog: (
      _,
      action: PayloadAction<LegalEntityCatalogSliceState>,
    ): LegalEntityCatalogSliceState => {
      return action.payload;
    },
  },
});

export const {
  updateCountries,
  updateOccupations,
  updateCountryStates,
  updateCities,
  updateTownships,
  updateBeneficiaryCountries,
  updateBeneficiaryCountryStates,
  updateBeneficiaryCities,
  updateBeneficiaryTownships,
  updateCivilStatus,
  updateBirthPlaces,
  updateRestrictedBirthPlaces,
  updateCountriesMap,
  updateBeneficiaryCountriesMap,
  updateOccupationsMap,
  updateBeneficiaryNationality,
  updateBeneficiaryNationalityMap,
  updateBeneficiaryBirthPlace,
  updateBeneficiaryBirthPlaceMap,
  updateBeneficiaryOccupations,
  updateEconomicActivities,
  updateBeneficiaryOccupationsMap,
  updateEconomicActivitiesMap,
  overwriteLegalEntityCatalog,
} = legalEntityCatalogSlice.actions;

export default legalEntityCatalogSlice.reducer;
